import React from "react";
import {
  useIsMobile,
  useMobileInteractions,
  useComponentState,
} from "../hooks";
import { FeatureCard } from "./FeatureCard";
import { SustFeatureExpander } from "./SustFeatureExpander";
import type { Feature, Certification } from "../types";
import { useMetricEmitter } from "../contexts/MetricEmitterContext";

interface FeaturesDisplayProps {
  features: Feature[];
  onCertificationSelect?: (
    certification: Certification,
    element?: HTMLElement,
  ) => void;
  className?: string;
}

export const FeaturesDisplay: React.FC<FeaturesDisplayProps> = ({
  features,
  onCertificationSelect,
  className = "",
}) => {
  const isMobile = useIsMobile();
  const { expander } = useComponentState();
  const { pointerHandlers, carouselRef, isDragging } =
    useMobileInteractions(isMobile);
  const metricEmitter = useMetricEmitter();

  // Track expander opens
  const handleExpanderOpen = (index: number) => {
    metricEmitter.trackClick("eo");
  };

  if (isMobile) {
    // Mobile: Horizontal scrollable carousel with touch support
    const hasSingleFeature = features?.length === 1;

    return (
      <div className={`features-display features-display--mobile ${className}`}>
        <div
          className={`features-carousel ${hasSingleFeature ? "features-carousel--single" : ""}`}
        >
          <ul
            ref={carouselRef}
            className={`features-carousel__container ${isDragging ? "features-carousel__container--dragging" : ""} ${hasSingleFeature ? "features-carousel__container--single" : ""}`}
            {...pointerHandlers}
          >
            {features.map((feature, index) => (
              <li key={index} className="features-carousel__item">
                <FeatureCard
                  feature={feature}
                  onCertificationSelect={onCertificationSelect}
                  className="features-carousel__card"
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className={`features-display features-display--desktop ${className}`}>
      <ul className="features-expander">
        {features.map((feature, index) => (
          <li key={index} className="features-expander__item">
            <SustFeatureExpander
              title={feature.displayName}
              headingLevel={3}
              open={expander.expandedItems.has(index)}
              onChange={(isOpen) =>
                expander.handleExpanderChange(index, isOpen)
              }
              onOpen={() => handleExpanderOpen(index)}
            >
              <div className="features-expander__content">
                <FeatureCard
                  feature={feature}
                  onCertificationSelect={onCertificationSelect}
                  className="features-expander__card"
                />
              </div>
            </SustFeatureExpander>
          </li>
        ))}
      </ul>
    </div>
  );
};
